import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAccountMasters, getAccountHeads, getAccountSubHeads, saveAccountSubHead, getNextAccountSubHeadCode } from "../../components/Api/GmsFinance";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import {
  hasPagePermissionWithAdmin,
  hasDeletePermissionWithAdmin,
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";

const initialForm = {
  accountSubHeadID: 0,
  accountSubHeadNameEn: "",
  accountSubHeadNameAr: "",
  accountMasterID: "",
  accountHeadID: "",
  status: true
};

const AccountSubHeadPage = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [subHeads, setSubHeads] = useState([]);
  const [heads, setHeads] = useState([]);
  const [masters, setMasters] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [listFilters, setListFilters] = useState({ accountMasterID: "", accountHeadID: "" });
  const [filterHeads, setFilterHeads] = useState([]);
  const [nextCode, setNextCode] = useState("");

  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pageRoute = '/GmsAccountSubHead';
  const hasView = hasPagePermissionWithAdmin(permissions, pageRoute);
  const hasEdit = hasEditOrHigherPermission(permissions, pageRoute);

  useEffect(() => {
    if (!hasView) {
      window.location.href = "/Dashboard";
    } else {
      loadData();
    }
  }, [hasView]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mastersData, subHeadsData] = await Promise.all([
        getAccountMasters(),
        getAccountSubHeads()
      ]);
      setMasters(mastersData);
      setSubHeads(subHeadsData);
    } catch (error) {
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.accountMasterID) {
      getAccountHeads(formData.accountMasterID).then(setHeads);
      if (!formData.accountSubHeadID) {
        setFormData(prev => ({ ...prev, accountHeadID: "" }));
      }
    } else {
      setHeads([]);
    }
  }, [formData.accountMasterID]);

  useEffect(() => {
    if (listFilters.accountMasterID) {
      getAccountHeads(listFilters.accountMasterID).then(setFilterHeads);
      setListFilters(prev => ({ ...prev, accountHeadID: "" }));
    } else {
      setFilterHeads([]);
    }
  }, [listFilters.accountMasterID]);

  useEffect(() => {
    if (formData.accountHeadID && !formData.accountSubHeadID) {
      setNextCode("");
      getNextAccountSubHeadCode(formData.accountHeadID)
        .then(res => setNextCode(res.code))
        .catch(err => console.error("Error fetching next code", err));
    } else {
      setNextCode("");
    }
  }, [formData.accountHeadID, formData.accountSubHeadID]);

  const filteredSubHeads = useMemo(() => {
    return subHeads.filter(s => {
      const matchMaster = !listFilters.accountMasterID || s.accountMasterID === parseInt(listFilters.accountMasterID);
      const matchHead = !listFilters.accountHeadID || s.accountHeadID === parseInt(listFilters.accountHeadID);
      return matchMaster && matchHead;
    });
  }, [subHeads, listFilters]);

  const handleEdit = (sub) => {
    setFormData({
      accountSubHeadID: sub.accountSubHeadID,
      accountSubHeadNameEn: sub.accountSubHeadNameEn,
      accountSubHeadNameAr: sub.accountSubHeadNameAr || "",
      accountMasterID: sub.accountMasterID || "",
      accountHeadID: sub.accountHeadID,
      status: sub.status
    });
    setActiveTab("1"); // Move to Add/Edit tab
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accountHeadID || !formData.accountSubHeadNameEn) {
      Swal.fire("Warning", "Please fill required fields", "warning");
      return;
    }

    setSaving(true);
    try {
      const response = await saveAccountSubHead(formData);
      if (response.success) {
        Swal.fire("Success", response.msg, "success");
        setFormData(initialForm);
        loadData();
        // setActiveTab("2"); // Stay on current tab
      } else {
        Swal.fire("Error", response.msg || "Failed to save", "error");
      }
    } catch (error) {
      console.error("Save Error:", error);
      Swal.fire("Error", "Server error", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Finance & Accounts" breadcrumbItem="Account Sub-Head" />

        <Row>
          <Col lg={12}>
            <Card className="shadow-sm border-0" style={{ borderRadius: '15px' }}>
              <CardBody>
                <div className="wizard clearfix">
                  <div className="steps clearfix">
                    <ul>
                      {hasEdit && (
                        <NavItem className={classnames({ current: activeTab === "1" })}>
                          <NavLink
                            className={classnames({ active: activeTab === "1" })}
                            onClick={() => {
                                if (activeTab === "2") setFormData(initialForm);
                                setActiveTab("1");
                            }}
                          >
                            <span className="number">1.</span> {formData.accountSubHeadID ? "Edit Sub-Head" : "Add Sub-Head"}
                          </NavLink>
                        </NavItem>
                      )}
                      <NavItem className={classnames({ current: activeTab === "2" })}>
                        <NavLink
                          className={classnames({ active: activeTab === "2" })}
                          onClick={() => setActiveTab("2")}
                        >
                          <span className="number">2.</span> Account Sub-Head List
                        </NavLink>
                      </NavItem>
                    </ul>
                  </div>

                  <div className="content clearfix mt-4">
                    <TabContent activeTab={activeTab}>
                      <TabPane tabId="1">
                        {hasEdit ? (
                          <div className="p-3">
                            <h5 className="mb-4 text-primary">{formData.accountSubHeadID ? "Update Existing Sub-Head" : "Add New Account Sub-Head"}</h5>
                            <Form onSubmit={handleSubmit}>
                              <Row>
                                <Col md={6}>
                                  <div className="mb-4">
                                    <Label className="fw-bold">Account Master <span className="text-danger">*</span></Label>
                                    <select
                                      className="form-select"
                                      value={formData.accountMasterID}
                                      onChange={(e) => setFormData({ ...formData, accountMasterID: e.target.value })}
                                      disabled={formData.accountSubHeadID !== 0}
                                      required
                                    >
                                      <option value="">Select Account Master</option>
                                      {masters.map(m => (
                                        <option key={m.accountMasterID} value={m.accountMasterID}>
                                          {m.accountMasterNameEn}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="mb-4">
                                    <Label className="fw-bold">Account Head <span className="text-danger">*</span></Label>
                                    <select
                                      className="form-select"
                                      value={formData.accountHeadID}
                                      onChange={(e) => setFormData({ ...formData, accountHeadID: e.target.value })}
                                      disabled={formData.accountSubHeadID !== 0 || !formData.accountMasterID}
                                      required
                                    >
                                      <option value="">Select Account Head</option>
                                      {heads.map(h => (
                                        <option key={h.accountHeadID} value={h.accountHeadID}>
                                          {h.accountHeadCode ? `${h.accountHeadCode} - ` : ""}{h.accountHeadNameEn}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </Col>
                                {formData.accountHeadID ? (
                                  <Col md={6}>
                                    <div className="mb-4">
                                      <Label className="fw-bold">Sub-Head Code</Label>
                                      <Input
                                        type="text"
                                        className="bg-light"
                                        value={formData.accountSubHeadID !== 0 
                                          ? (subHeads.find(s => s.accountSubHeadID === formData.accountSubHeadID)?.accountSubHeadCode || "") 
                                          : (nextCode || "Fetching...")}
                                        readOnly
                                      />
                                    </div>
                                  </Col>
                                ) : null}
                                <Col md={6}>
                                  <div className="mb-4">
                                    <Label className="fw-bold">Sub-Head Name (English) <span className="text-danger">*</span></Label>
                                    <Input
                                      type="text"
                                      placeholder="Enter Sub-Head Name"
                                      value={formData.accountSubHeadNameEn}
                                      onChange={(e) => setFormData({ ...formData, accountSubHeadNameEn: e.target.value })}
                                      required
                                    />
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="mb-4">
                                    <Label className="fw-bold">Sub-Head Name (Arabic)</Label>
                                    <Input
                                      type="text"
                                      placeholder="Enter Sub-Head Name in Arabic"
                                      value={formData.accountSubHeadNameAr}
                                      onChange={(e) => setFormData({ ...formData, accountSubHeadNameAr: e.target.value })}
                                    />
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="mb-4 d-flex align-items-center h-100 mt-2">
                                    <div className="form-check form-switch form-switch-md">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="statusSwitchSub"
                                        checked={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                                      />
                                      <Label className="form-check-label ms-2" htmlFor="statusSwitchSub">Active Status</Label>
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              <div className="mt-4 d-flex gap-2">
                                <Button color="primary" type="submit" disabled={saving}>
                                  {saving && <ClipLoader size={15} color="#fff" className="me-1" />}
                                  {formData.accountSubHeadID ? "Update Sub-Head" : "Save Sub-Head"}
                                </Button>
                                <Button color="light" type="button" onClick={() => { setFormData(initialForm); setActiveTab("2"); }}>
                                  Cancel
                                </Button>
                              </div>
                            </Form>
                          </div>
                        ) : (
                          <div className="alert alert-warning">You do not have permission to add or edit.</div>
                        )}
                      </TabPane>

                      <TabPane tabId="2">
                        <div className="p-2">
                          <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="text-primary mb-0">All Account Sub-Heads</h5>
                            <Button
                              color="soft-primary"
                              onClick={() => setShowFilters(!showFilters)}
                              className="d-flex align-items-center gap-2"
                            >
                              <i className={`bx ${showFilters ? 'bx-chevron-up' : 'bx-filter'} font-size-18`}></i>
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>

                          {showFilters && (
                            <Card className="bg-light border-0 mb-4" style={{ borderRadius: '12px' }}>
                              <CardBody className="p-3">
                                <Row className="align-items-end">
                                  <Col md={4}>
                                    <Label className="fw-bold font-size-13 text-muted">Account Master</Label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={listFilters.accountMasterID}
                                      onChange={(e) => setListFilters({ ...listFilters, accountMasterID: e.target.value })}
                                    >
                                      <option value="">All Categories</option>
                                      {masters.map(m => (
                                        <option key={m.accountMasterID} value={m.accountMasterID}>
                                          {m.accountMasterNameEn}
                                        </option>
                                      ))}
                                    </select>
                                  </Col>
                                  <Col md={4}>
                                    <Label className="fw-bold font-size-13 text-muted">Account Head</Label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={listFilters.accountHeadID}
                                      onChange={(e) => setListFilters({ ...listFilters, accountHeadID: e.target.value })}
                                      disabled={!listFilters.accountMasterID}
                                    >
                                      <option value="">All Heads</option>
                                      {filterHeads.map(h => (
                                        <option key={h.accountHeadID} value={h.accountHeadID}>
                                          {h.accountHeadCode ? `${h.accountHeadCode} - ` : ""}{h.accountHeadNameEn}
                                        </option>
                                      ))}
                                    </select>
                                  </Col>
                                  <Col md={2}>
                                    <Button 
                                      color="light" 
                                      size="sm" 
                                      block 
                                      onClick={() => setListFilters({ accountMasterID: "", accountHeadID: "" })}
                                    >
                                      Reset
                                    </Button>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          )}

                          {loading ? (
                            <div className="text-center py-5">
                              <ClipLoader color="#556ee6" size={50} />
                              <p className="mt-3 text-muted">Fetching records...</p>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <Table className="table table-bordered table-hover align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: "80px" }}>Sr.No</th>
                                    <th>Head Code</th>
                                    <th>Account Head</th>
                                    <th>Sub-Head Code</th>
                                    <th>Sub-Head Name (EN)</th>
                                    <th>Sub-Head Name (AR)</th>
                                    <th>Status</th>
                                    {hasEdit && <th className="text-center">Action</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredSubHeads.length > 0 ? (
                                    filteredSubHeads.map((sub, idx) => (
                                      <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        <td><Badge color="soft-primary">{sub.accountHeadCode}</Badge></td>
                                        <td>{sub.accountHeadNameEn}</td>
                                        <td><span className="fw-bold text-info">{sub.accountSubHeadCode}</span></td>
                                        <td>{sub.accountSubHeadNameEn}</td>
                                        <td>{sub.accountSubHeadNameAr || "-"}</td>
                                        <td>
                                          <Badge color={sub.status ? "success" : "danger"} className="p-2">
                                            {sub.status ? "Active" : "Inactive"}
                                          </Badge>
                                        </td>
                                        {hasEdit && (
                                          <td className="text-center">
                                            <Button
                                              size="sm"
                                              color="soft-primary"
                                              className="me-2"
                                              onClick={() => handleEdit(sub)}
                                            >
                                              <i className="bx bxs-edit font-size-16"></i> Edit
                                            </Button>
                                          </td>
                                        )}
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="8" className="text-center py-4 text-muted">No records found</td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </div>
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AccountSubHeadPage;
